import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/auth';
import { config } from '../config/env';
import { Sparkles, Zap, Shield, Eye, EyeOff, Check, Smartphone, Lock } from 'lucide-react';
import './Auth.css';

function Signup() {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        fullName: '',
        workspaceName: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('signup');
    const [successMessage, setSuccessMessage] = useState('');
    const { register } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Pre-fill email from invitation flow
    useEffect(() => {
        if (location.state?.email) {
            setFormData(prev => ({ ...prev, email: location.state.email }));
        }
        if (location.state?.message) {
            setSuccessMessage(location.state.message);
        }
    }, [location]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await register(formData);
            // Check if there's a returnTo path from invitation flow
            const returnTo = location.state?.returnTo || '/';
            navigate(returnTo);
        } catch (err) {
            setError(err.response?.data?.error || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">
            {/* Left Side - Signup Form */}
            <div className="auth-left">
                <div className="auth-logo">
                    <img
                        src={`${process.env.PUBLIC_URL}/rova-ai-logo-wordmark-white.png`}
                        alt="ROVA AI"
                        className="logo-wordmark"
                        style={{ height: '32px', width: 'auto' }}
                    />
                </div>

                <div className="auth-content">
                    <h1 className="auth-title">Create your account</h1>
                    <p className="auth-subtitle">
                        Start automating your mobile tests with AI in minutes.
                    </p>

                    {config.AUTH_MODE === 'external' ? (
                        <div className="auth-external-signup">
                            <p className="auth-description" style={{ marginBottom: '24px', color: 'var(--text-secondary)' }}>
                                Account creation is managed by your organization. Please log in with your Scandium account to access the platform.
                            </p>

                            <div className="social-buttons">
                                <button
                                    type="button"
                                    className="btn btn-primary social-btn btn-full"
                                    onClick={() => authService.loginWithSSO()}
                                    style={{ justifyContent: 'center' }}
                                >
                                    <Shield size={18} />
                                    Log in with Scandium SSO
                                </button>
                            </div>

                            <div className="auth-footer" style={{ marginTop: '24px' }}>
                                <p>Already have an account? <Link to="/login">Sign In</Link></p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Tabs */}
                            <div className="auth-tabs">
                                <Link to="/login" className={`btn auth-tab ${activeTab === 'signin' ? 'active' : ''}`}>
                                    Sign In
                                </Link>
                                <Link to="/signup" className={`btn auth-tab ${activeTab === 'signup' ? 'active' : ''}`}>
                                    Create Account
                                </Link>

                            </div>

                            {successMessage && <div className="auth-success">{successMessage}</div>}
                            {error && <div className="auth-error">{error}</div>}

                            <form onSubmit={handleSubmit} className="auth-form">
                                <div className="form-group">
                                    <label className="label">Full Name</label>
                                    <input
                                        type="text"
                                        name="fullName"
                                        className="input"
                                        value={formData.fullName}
                                        onChange={handleChange}
                                        placeholder="John Doe"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="label">Work Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        className="input"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="name@company.com"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="label">Password</label>
                                    <div className="password-input">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            className="input"
                                            value={formData.password}
                                            onChange={handleChange}
                                            placeholder="Create a strong password"
                                            required
                                            minLength={6}
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="label">Workspace Name</label>
                                    <input
                                        type="text"
                                        name="workspaceName"
                                        className="input"
                                        value={formData.workspaceName}
                                        onChange={handleChange}
                                        placeholder="My Company (optional)"
                                    />
                                </div>

                                <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading}>
                                    {loading ? 'Creating account...' : 'Create Account'}
                                </button>
                            </form>
                        </>
                    )}

                    {/* <div className="divider-text">Or continue with</div>

                    <div className="social-buttons">
                        <button type="button" className="btn btn-secondary social-btn">
                            <img src="https://www.google.com/favicon.ico" alt="Google" width="18" />
                            Google
                        </button>
                        <button type="button" className="btn btn-secondary social-btn">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                            </svg>
                            GitHub
                        </button>
                    </div> */}

                    <p className="auth-footer">
                        By creating an account, you agree to our <a href="#">Terms of Service</a> and <a href="#">Privacy Policy</a>.
                    </p>
                </div>
            </div>

            {/* Right Side - Value Props */}
            <div className="auth-right">
                <div className="value-props">
                    <div className="hero-icon">
                        <Sparkles size={32} />
                    </div>
                    <h2 className="hero-title">
                        Start testing smarter today
                    </h2>

                    <div className="signup-benefits">
                        <div className="benefit-item">
                            <Check size={18} className="benefit-check" />
                            <span>Free 14-day trial with all features</span>
                        </div>
                        <div className="benefit-item">
                            <Check size={18} className="benefit-check" />
                            <span>No credit card required</span>
                        </div>
                        <div className="benefit-item">
                            <Check size={18} className="benefit-check" />
                            <span>Cancel anytime</span>
                        </div>
                    </div>

                    <div className="feature-list">
                        <div className="feature-item">
                            <div className="feature-icon">
                                <Zap size={18} />
                            </div>
                            <div>
                                <h3 className="feature-title">10x Faster Execution</h3>
                                <p className="feature-desc">
                                    Our autonomous agents run parallel tests across thousands of device configurations instantly.
                                </p>
                            </div>
                        </div>

                        <div className="feature-item">
                            <div className="feature-icon">
                                <Sparkles size={18} />
                            </div>
                            <div>
                                <h3 className="feature-title">AI-Powered Testing</h3>
                                <p className="feature-desc">
                                    Intelligent and goal driven automation that adapts to your app's unique flows.
                                </p>
                            </div>
                        </div>

                        <div className="feature-item">
                            <div className="feature-icon">
                                <Smartphone size={18} />
                            </div>
                            <div>
                                <h3 className="feature-title">Cross Platform</h3>
                                <p className="feature-desc">
                                    Seamlessly test across Android and iOS devices with a single unified platform.
                                </p>
                            </div>
                        </div>

                        <div className="feature-item">
                            <div className="feature-icon">
                                <Shield size={18} />
                            </div>
                            <div>
                                <h3 className="feature-title">Security</h3>
                                <p className="feature-desc">
                                    Enterprise-grade security with SOC2 compliance and end-to-end encryption.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="social-proof">
                        <div className="avatars">
                            <div className="avatar">👤</div>
                            <div className="avatar">👤</div>
                            <div className="avatar">👤</div>
                        </div>
                        <span>Used by 10,000+ developers</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Signup;
